import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa"; // Import arrow icons
import { FaPhone, FaWhatsapp } from "react-icons/fa";
import { logFirebaseEvent } from "../../Firebase"; // Ensure the correct import path


export default function PropertyDetails() {
    const { id } = useParams();
    const initialData = window.__INITIAL_DATA__ || null;
    const [property, setProperty] = useState(initialData);
    const [loading, setLoading] = useState(!initialData);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!initialData) {
            fetch(`https://api-sumer.com/api/properties/${id}`)
                .then((res) => res.json())
                .then((data) => {
                    setProperty(data.data);
                    console.log("Property Data:", property);

                    setLoading(false);
                })
                .catch(() => {
                    setError("Failed to fetch property data");
                    setLoading(false);
                });
        }
    }, [id, initialData]);

    if (loading) return <p className="text-center text-gray-500 text-lg">Loading...</p>;
    if (error) return <p className="text-center text-red-500 text-lg">{error}</p>;

    // Custom Arrow Components
    const CustomPrevArrow = ({ onClick }) => (
        <div
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-4 rounded-full cursor-pointer z-10 shadow-lg hover:bg-blue-700"
            onClick={onClick}
        >
            <FaArrowLeft size={15} />
        </div>
    );

    const CustomNextArrow = ({ onClick }) => (
        <div
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-4 rounded-full cursor-pointer z-10 shadow-lg hover:bg-blue-700"
            onClick={onClick}
        >
            <FaArrowRight size={15} />
        </div>
    );

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: false,
        arrows: true,
        prevArrow: <CustomPrevArrow />,
        nextArrow: <CustomNextArrow />,
    };



    const handleClick = async (type) => {
        if (!property.id) return;

        logFirebaseEvent(`property_${type}_click`, { property_id: property.id });
        const url = `https://api-sumer.com/api/properties/${property.id}/${type}_clicks/increment`;

        try {
            await fetch(url, { method: "POST" });
        } catch (error) {
            console.error("Error tracking click:", error);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-6 pt-20">

            <Helmet>
                <title>{property.title}</title>
            </Helmet>

            <h1 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">{property.title}</h1>
            <p className="text-gray-700 mb-6 leading-relaxed">{property.description}</p>

            <div className="relative mb-6">
                <Slider {...settings}>
                    {property.images.map((image, index) => (
                        <div key={`image-${index}`}>
                            <img
                                src={image.url}
                                alt={`Property Image ${index + 1}`}
                                className="rounded-lg shadow-md w-full h-[500px] object-cover"
                            />
                        </div>
                    ))}
                    {property.videos && property.videos.map((video, index) => (
                        <div key={`video-${index}`} className="flex justify-center">
                            <video
                                controls
                                className="rounded-lg shadow-md w-full h-[500px] object-cover"
                            >
                                <source src={video.url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    ))}
                </Slider>
            </div>
            <div className="mt-6 p-4 bg-gray-100 rounded-lg shadow-sm grid grid-cols-2 gap-4">
                {property.price && (
                    <p className="font-semibold text-lg">السعر: <span className="text-blue-600">{property.price} IQD</span></p>
                )}
                {property.city?.new_name?.ar && (
                    <p className="font-semibold text-lg">المدينة: <span className="text-gray-800">{property.city.new_name.ar}</span></p>
                )}
                {property.neighborhood?.new_name?.ar && (
                    <p className="font-semibold text-lg">الحي: <span className="text-gray-800">{property.neighborhood.new_name.ar}</span></p>
                )}
                {property.size_value && property.size_unit && (
                    <p className="font-semibold text-lg">الحجم: <span className="text-gray-800">{property.size_value} {property.size_unit}</span></p>
                )}
                {property.bedrooms !== null && property.bedrooms !== undefined && (
                    <p className="font-semibold text-lg">غرف النوم: <span className="text-gray-800">{property.bedrooms}</span></p>
                )}
                {property.bathrooms !== null && property.bathrooms !== undefined && (
                    <p className="font-semibold text-lg">الحمامات: <span className="text-gray-800">{property.bathrooms}</span></p>
                )}
            </div>

            <div className="mt-6 flex flex-col items-center gap-4">
                {property.phone && (
                    <div className="flex gap-4">
                        <a
                            href={`tel:${property.phone}`}
                            onClick={() => handleClick("call")}
                            className="bg-green-600 text-white px-6 py-3 rounded-full shadow-md text-lg font-semibold flex items-center gap-2 transition-transform transform hover:scale-105"
                        >
                            <FaPhone /> Call
                        </a>
                        <a
                            href={`https://wa.me/${property.whatsapp}`}
                            onClick={() => handleClick("whatsapp")}
                            className="bg-green-500 text-white px-6 py-3 rounded-full shadow-md text-lg font-semibold flex items-center gap-2 transition-transform transform hover:scale-105"
                        >
                            <FaWhatsapp /> WhatsApp
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
